/**
 * Block 12 PR-D-A Phase D5 Step 8 — derive initial QuoteState from anketa project.
 *
 * Когда пользователь жмёт «Сохранить и сделать коммерческое предложение» в анкете,
 * AnketaFormContainer вызывает `navigate('/calculator/quote?projectId=<uuid>')`.
 * QuotePageContainer на mount читает projectId из query, загружает project через
 * `getProject(id)` и derive'ит initial QuoteState с подтянутыми furnitureType
 * (через `ANKETA_TO_FURNITURE_TYPE`) + computedTotal (как placeholder для маржи)
 * + composition rows partially derived через `deriveCompositionFromAnketa`
 * (PR-H1 #5 fix — раньше все 17 rows были blank, B-118 deferred). Conditions +
 * Contacts default'ятся через INIT_DEFAULTS reducer actions из секций.
 *
 * **Graceful degradation:** если project не найден / IDB unavailable / wrong
 * anketaType — функция возвращает `null` и Container fallback'ит на blank state
 * (console.warn в DEV). НЕ throw наружу.
 *
 * Reference: PRD §10.12.10 «Источники значения» line 355, TZ Phase D5 Step 8.
 * Related: BACKLOG B-118 (composition mapping в `deriveCompositionFromAnketa`
 * — partial coverage, future iterations добавят оставшиеся 8 rows если
 * соответствующие поля будут persisted в payload schema).
 */

import { isCalculatorAnketaType } from '@pomoshnik/shared';
import type { CalculatorAnketaType } from '@pomoshnik/shared';
import type { ProjectRecord } from '@/lib/offline/idb/database';
import { getProject } from '@/lib/offline/idb/projects';
import { DEFAULT_COMPOSITION_ROWS } from '../compositionRows';
import { DEFAULT_CONTACTS } from '../contactRows';
import { ANKETA_TO_FURNITURE_TYPE } from '../furnitureTypes';
import type { CompositionRow, QuoteState } from '../types';
import { COMPOSITION_RULES, type CompositionRule } from './compositionRules';

/**
 * PR-H1 Round 4 — rules-driven mapping AnketaState (через сохранённый
 * ProjectRecord) → 17 composition rows на основе `COMPOSITION_RULES` whitelist
 * per anketa-type. Полностью заменяет Round 1-3 hardcoded switch-by-rowId.
 *
 * **Whitelist semantics (Aibek's Round 4 правила):** для каждого
 * `CalculatorAnketaType` определён explicit набор rules в `compositionRules.ts`.
 * Rows вне whitelist для текущего anketa-type — остаются blank (filter
 * `isCompositionRowEmpty` в КП Spec скрывает). `fittings` row не в одном
 * whitelist — никогда не seeded автоматически.
 *
 * **Condition checks per source:**
 *   - `'material-payload'` (facades): `project.data[materialPriceField] > 0`.
 *     Если condition pass → `seeded: true`, brand из `brandSelections[rowId]`,
 *     itemType пустой (facade kind не в payload — заполняется в КП Spec вручную).
 *   - `'free-item'` (countertop, additionals, filling, lighting): найти freeItem
 *     с `name.startsWith(prefix)`, проверить `qty > 0 AND price > 0`.
 *     Если condition pass → `seeded: true`, brand из
 *     `brandSelections[anketaRowId ?? rowId]`, itemType: для `countertop` ←
 *     `params.countertopKind`; для `facade-radius` ← extract selection из
 *     encoded name `"Фасад радиусный — <selection>"`; для остальных — пустой.
 *
 * **Round 4 поведенческие отличия от Round 3:**
 *   - Per-anketa-type whitelist filtering (rules table).
 *   - Condition `qty>0 AND price>0` для всех free-item rules (Round 3 проверял
 *     только наличие freeItem).
 *   - Added `facades` material-payload mapping — раньше facades всегда оставался
 *     blank, теперь seeded если `facadePrice > 0`.
 *   - Added `countertop` через free-item + condition (Round 1-3 проверял только
 *     `params.countertopKind` string presence, без price check).
 *   - Added Section Г filling (rod / pantograph / trousers / ironing-board /
 *     shoe-rack / vacuum-holder) — Round 1-3 не encoded в payload (B-118 closed).
 *   - Added Section Д lighting — Round 1-3 не encoded в payload (B-118 closed).
 *
 * **Backward-compat:** Round 1-2 payloads без `brandSelections` field → safe
 * fallback (empty record), rows seeded с пустым brand. Round 1-3 payloads без
 * countertop/filling/lighting freeItems → соответствующие rows blank (condition fail).
 *
 * **Pristine-only semantics:** функция вызывается из `seedQuoteFromAnketa` →
 * QuotePageContainer Priority 3, который запускается ТОЛЬКО когда Priority 1
 * (Zustand) + Priority 2 (IDB drafts) + Priority 2.5 (savedQuote) пустые. Это
 * effectively guards против overwrite user edits — после первой autosave (300ms)
 * Priority 1/2 hit'ят и seed skipped.
 *
 * Reference: TZ Block_12_PR_H1_hotfix_round4_start.md TL;DR + compositionRules.ts.
 */
function deriveCompositionFromAnketa(
  project: ProjectRecord,
  defaults: ReadonlyArray<CompositionRow>,
): CompositionRow[] {
  const anketaType = project.furnitureType as CalculatorAnketaType;
  const rules = COMPOSITION_RULES[anketaType] ?? [];

  // params в ProjectRecord.data — discriminated union CalculatorParams (по
  // anketaType). Здесь type-erased до Record<string, unknown> с runtime guards
  // (typeof string) при чтении. Defensive против IDB schema drift / corrupted
  // records (P-009 spirit — read-only narrow at boundary).
  const params = project.data.params as Record<string, unknown> | undefined;
  // PR-H1 Round 2 (ADR-0026) — brand storage в map `brandSelections: Record<rowId, brand>`.
  // Backward-compat: Round 1 payloads без field → empty Record.
  const brandSelections =
    (project.data.brandSelections as Record<string, string> | undefined) ?? {};
  const freeItems = project.data.freeItems ?? [];
  // ProjectRecord.data доступ по materialPriceField name — type-erased read-only
  // lookup, Defensive ?? 0.
  const payloadRecord = project.data as unknown as Record<string, unknown>;

  return defaults.map((defaultRow) => {
    const rule = rules.find((r) => r.rowId === defaultRow.id);
    if (!rule) return { ...defaultRow }; // not in whitelist → blank

    const brandKey = rule.anketaRowId ?? rule.rowId;
    const brand = typeof brandSelections[brandKey] === 'string' ? brandSelections[brandKey] : '';

    if (rule.source === 'material-payload') {
      return seedFromMaterialPayload(defaultRow, rule, payloadRecord, brand);
    }
    return seedFromFreeItem(defaultRow, rule, freeItems, params, brand);
  });
}

/**
 * Material-payload rule (facades): seed `defaultRow` если `payload[field] > 0`.
 * Brand из brandSelections (rule.rowId / anketaRowId). itemType пустой —
 * facade kind не в payload, заполняется вручную в КП Spec.
 */
function seedFromMaterialPayload(
  defaultRow: CompositionRow,
  rule: CompositionRule,
  payloadRecord: Record<string, unknown>,
  brand: string,
): CompositionRow {
  const field = rule.materialPriceField;
  if (!field) return { ...defaultRow };
  const raw = payloadRecord[field];
  const price = typeof raw === 'number' ? raw : 0;
  if (price <= 0) return { ...defaultRow };
  return { ...defaultRow, seeded: true, brand };
}

/**
 * Free-item rule: seed `defaultRow` если найден freeItem с `name.startsWith(prefix)`
 * И condition `quantity > 0 AND price > 0`. itemType extracted для countertop
 * (params.countertopKind) / facade-radius (encoded suffix); остальные — пустой.
 */
function seedFromFreeItem(
  defaultRow: CompositionRow,
  rule: CompositionRule,
  freeItems: ReadonlyArray<{ name?: unknown; quantity?: number; price?: number }>,
  params: Record<string, unknown> | undefined,
  brand: string,
): CompositionRow {
  const prefix = rule.freeItemPrefix;
  if (!prefix) return { ...defaultRow };
  const freeItem = freeItems.find(
    (it) => typeof it.name === 'string' && it.name.startsWith(prefix),
  );
  if (!freeItem || typeof freeItem.name !== 'string') return { ...defaultRow };
  if ((freeItem.quantity ?? 0) <= 0 || (freeItem.price ?? 0) <= 0) {
    return { ...defaultRow }; // condition fails — row blank
  }

  let itemType = '';
  if (defaultRow.id === 'countertop') {
    if (params && typeof params.countertopKind === 'string') itemType = params.countertopKind;
  } else if (defaultRow.id === 'facade-radius') {
    const sepIdx = freeItem.name.indexOf(' — ');
    if (sepIdx >= 0) itemType = freeItem.name.slice(sepIdx + 3);
  }
  // Все остальные rows (vitrine, metallic-legs, basket-pullout, rod, pantograph,
  // trousers, ironing-board, shoe-rack, vacuum-holder, foldable-facade,
  // trash-bin, laundry-basket, lighting) — itemType пустой per Aibek's Round 3
  // правило «не дублировать имя».
  return { ...defaultRow, seeded: true, itemType, brand };
}

/**
 * Partial `QuoteState` payload готовый к dispatch HYDRATE. Container будет
 * мерджить с initialQuoteState (через reducer HYDRATE которое выставит
 * hydrationStatus='ready').
 *
 * Возвращает `null` если seed невозможен (project не найден / IDB ошибка /
 * project.furnitureType не валиден).
 */
export type AnketaSeedPayload = Partial<
  Pick<
    QuoteState,
    | 'projectId'
    | 'furnitureType'
    | 'compositionRows'
    | 'costWithMargin'
    | 'executorContacts'
    /** PR-H2 ADR-0027 — `Date.now()` на момент seed call. */
    | 'seededAt'
    /** PR-H2 ADR-0027 — `project.updatedAt` (Anketa) на момент seed. */
    | 'lastAnketaUpdatedAt'
  >
>;

export async function seedQuoteFromAnketa(projectId: string): Promise<AnketaSeedPayload | null> {
  let project;
  try {
    project = await getProject(projectId);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[seedQuoteFromAnketa] getProject failed', err);
    }
    return null;
  }
  if (!project) {
    if (import.meta.env.DEV) {
      console.warn('[seedQuoteFromAnketa] project not found', projectId);
    }
    return null;
  }

  // ProjectRecord.furnitureType хранит anketaType (kebab-case, e.g. 'kitchen').
  // Naming overload — поле ProjectRecord так называется per Phase A1 schema,
  // но семантика = anketaType. Phase D5 type-narrow через guard.
  const anketaType = project.furnitureType;
  if (!isCalculatorAnketaType(anketaType)) {
    if (import.meta.env.DEV) {
      console.warn(
        '[seedQuoteFromAnketa] project.furnitureType не валидный CalculatorAnketaType',
        anketaType,
      );
    }
    return null;
  }

  // PR-H7 (ADR-0030 §8) — «Название мебели» field removed. executorContacts
  // seeded из DEFAULT_CONTACTS (2-row default: Имя, Телефон). project.itemName
  // больше не пишется в quote payload — для contract instance name есть
  // отдельная responsibility в Block 13.
  const executorContacts = DEFAULT_CONTACTS.map((row) => ({ ...row }));

  return {
    projectId,
    furnitureType: ANKETA_TO_FURNITURE_TYPE[anketaType],
    compositionRows: deriveCompositionFromAnketa(project, DEFAULT_COMPOSITION_ROWS),
    costWithMargin: Number.isFinite(project.total) ? Math.round(project.total) : null,
    executorContacts,
    seededAt: Date.now(),
    lastAnketaUpdatedAt: project.updatedAt,
  };
}
